import React from "react";

type Props = {
  /** Primary handle (`player_name`) — large Anton uppercase. */
  playerName: string;
  /** Optional real/friendly name — secondary style when present. */
  displayName?: string | null;
  /** Extra classes on the outer stack (alignment, width). */
  className?: string;
  /** Render the primary name (e.g. wrap in a button). Defaults to a plain span. */
  renderPlayerName?: (label: string) => React.ReactNode;
};

/**
 * Profile / friends name stack: player handle on top, optional display_name beneath.
 */
export function ProfileNameStack({
  playerName,
  displayName,
  className = "",
  renderPlayerName,
}: Props) {
  const primary = String(playerName || "Player").trim() || "Player";
  const secondary = typeof displayName === "string" ? displayName.trim() : "";
  const showSecondary =
    Boolean(secondary) && secondary.toLowerCase() !== primary.toLowerCase();

  return (
    <div className={`flex min-w-0 flex-col items-center ${className}`.trim()}>
      {renderPlayerName ? (
        renderPlayerName(primary)
      ) : (
        <span className="w-full max-w-full truncate text-center font-anton text-[20px] font-normal uppercase leading-[26px] tracking-normal text-white [leading-trim:none]">
          {primary.toUpperCase()}
        </span>
      )}
      {showSecondary ? (
        <span className="mt-0.5 max-w-full truncate text-center font-chakra text-[12px] font-normal normal-case leading-[16px] tracking-normal text-white/55 [leading-trim:none]">
          {secondary}
        </span>
      ) : null}
    </div>
  );
}

/** Compact two-line name for friends list / invite rows (left-aligned). */
export function FriendNameStack({
  playerName,
  displayName,
  className = "",
  playerNameClassName = "truncate text-left font-anton text-[15px] leading-tight text-white max-[640px]:text-[13px] max-[420px]:text-[11px]",
  displayNameClassName = "mt-0.5 truncate text-left font-chakra text-[11px] normal-case leading-none text-white/50",
}: {
  playerName: string;
  displayName?: string | null;
  className?: string;
  playerNameClassName?: string;
  displayNameClassName?: string;
}) {
  const primary = String(playerName || "Player").trim() || "Player";
  const secondary = typeof displayName === "string" ? displayName.trim() : "";
  const showSecondary =
    Boolean(secondary) && secondary.toLowerCase() !== primary.toLowerCase();

  return (
    <span className={`flex min-w-0 flex-col ${className}`.trim()}>
      <span className={playerNameClassName}>{primary}</span>
      {showSecondary ? <span className={displayNameClassName}>{secondary}</span> : null}
    </span>
  );
}
